/** Import a CSV and export one (plan E05-T05): the consents' and the do-not-call list's. */
import { useMutation } from "@tanstack/react-query";
import { Download, Upload } from "lucide-react";
import { useId, useRef } from "react";
import { useT } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { save, stamped } from "./files";

export interface Imported {
  done: string; // "12 numbers added", in the reader's words
  errors: { line: number; error: string }[];
}

export function ImportExport({ name, canImport, send, fetchCsv }: {
  name: string; // the file's prefix: consents, do-not-call
  canImport: boolean;
  send: (csv: string) => Promise<Imported>;
  fetchCsv: () => Promise<string>;
}) {
  const t = useT();
  const id = useId();
  const picker = useRef<HTMLInputElement>(null);
  const upload = useMutation({ mutationFn: async (file: File) => send(await file.text()) });
  const download = useMutation({
    mutationFn: async () => save(new Blob([await fetchCsv()], { type: "text/csv" }), stamped(name)),
  });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {canImport && (
          <>
            <input id={`${id}-file`} ref={picker} type="file" accept=".csv,text/csv" className="sr-only" tabIndex={-1}
              aria-hidden="true"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = ""; // the same file may be sent again
              }} />
            <Button variant="secondary" disabled={upload.isPending} onClick={() => picker.current?.click()}>
              <Upload size={16} aria-hidden="true" />
              {t("compliance.csv.import")}
            </Button>
          </>
        )}
        <Button variant="secondary" disabled={download.isPending} onClick={() => download.mutate()}>
          <Download size={16} aria-hidden="true" />
          {t("compliance.csv.export")}
        </Button>
      </div>
      <div aria-live="polite">
        {upload.data && (
          <div className="rounded-md border border-[var(--color-border)] p-3 text-sm">
            <p>{upload.data.done}</p>
            {upload.data.errors.length > 0 && (
              <>
                <p className="mt-2 font-medium">{t("compliance.csv.problems", { n: upload.data.errors.length })}</p>
                <ul className="mt-1 list-disc pl-5">
                  {upload.data.errors.map((p) => (
                    <li key={p.line}>{t("compliance.csv.line", { line: p.line, error: p.error })}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
      {upload.isError && <ErrorState error={upload.error} />}
      {download.isError && <ErrorState error={download.error} />}
    </div>
  );
}
